import React, { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useModal } from 'store/modal';
import { useActiveClientContext } from 'store/active-client';

import { isIntigerOrFloat } from 'utils/helpers/leaves';
import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';

import UpdateEntity from 'components/crud/UpdateEntity';
import { ISelectOptionProps } from 'components/form/types';
import EmployeeForm from '../../components/EmployeeForm';
import { IEmployee } from '../../types';
import nkpdCodes from '../../nkpd.codes.json';
import EmployeeFormMulti from '../../components/EmployeeFormMulti';
import { formatDataOnSubmit } from '../../dataFormatters';
import { IDropdowns, IInputs } from '../../components/types';
import EmployeeOrganizeGroups from '../../components/EmployeeOrganizeGroups';
import EmployeeGroupRoleLimits from '../../components/EmployeeGroupRoleLimits';

interface IProps {
  user: IEmployee;
  roleOptions: ISelectOptionProps[];
  userDataOptions: IDropdowns;
  onSpecialViewChange?: (isSpecialView: boolean) => void;
}

const EditForm: FC<IProps> = ({ user, roleOptions, userDataOptions, onSpecialViewChange }: IProps) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { showModal } = useModal();

  const { client } = useActiveClientContext();

  const [isOrganizeGroup, setIsOrganizeGroup] = useState(false);
  const [isManageGroupRoleLimits, setIsManageGroupRoleLimits] = useState(false);

  const location = useLocation();
  const type = userDataOptions.type.find(option => user!.type === option.value);

  const matchingCode = nkpdCodes.find((code: any) => code[0] === user.nkpd_code || code[1] === user.nkpd_code);
  const label = matchingCode ? `${matchingCode[0]} - ${matchingCode[1]}` : user.nkpd_code;
  const nkpdCode: ISelectOptionProps = {
    value: user.nkpd_code,
    label,
  };

  const education = userDataOptions.education.find(option => user!.education === option.value);
  const accountStatus = userDataOptions.account_status.find(option => user!.account_status === option.value);
  const groups = userDataOptions.group.filter(gr => user.groups.find(grOpt => grOpt.id === gr.value));
  const groupRights = userDataOptions.group.filter(gr => user.group_rights.find(grOpt => grOpt.id === gr.value));
  const roles = roleOptions.filter(role => user.roles.find(r => r.id === role.value));
  const workingHours = userDataOptions.working_hours.find(
    option => user!.working_hours?.toString() === option.value.toString()
  );
  const workingType = userDataOptions.working_type.find(option => user!.working_type === option.value);
  const leaveNoticeDeadline = userDataOptions.leave_notice_deadline.find(
    option => user!.leave_notice_deadline === option.value
  );
  const probationPeriod = userDataOptions.probation_period.find(option => user!.probation_period === option.value);
  const probationBeneficiary = userDataOptions.probation_beneficiary.find(
    option => user!.probation_beneficiary === option.value
  );
  const legalRelationship = userDataOptions.legal_relationship?.find(
    option => user!.legal_relationship === option.value
  );
  const administrativeUnit = userDataOptions.administrative_unit?.find(
    option => user!.administrative_unit === option.value
  );
  const positionLevel = userDataOptions.position_level?.find(option => user!.position_level === option.value);
  const positionLevelInfoConfig: {
    [key: string]: ISelectOptionProps | undefined;
  } = {
    leading: userDataOptions.position_level_leading?.find(option => user!.position_level_info === option.value),
    expert: userDataOptions.position_level_expert?.find(option => user!.position_level_info === option.value),
    specialist: userDataOptions.position_level_specialist?.find(option => user!.position_level_info === option.value),
  };

  const positionLevelInfo: ISelectOptionProps | undefined = positionLevelInfoConfig[user!.position_level];

  const educationalLevel = userDataOptions.educational_level?.find(option => user!.educational_level === option.value);
  const rang = userDataOptions.rang?.find(option => user!.rang === option.value);
  const monthlySalaryLevel = userDataOptions.monthly_salary_level?.find(
    option => user!.monthly_salary_level === option.value
  );
  const monthlySalaryLevelDegree = userDataOptions.monthly_salary_level_degree?.find(
    option => user!.monthly_salary_level_degree === option.value
  );
  const individualMonthlySalary = userDataOptions.individual_monthly_salary?.find(
    option => user!.individual_monthly_salary === option.value
  );

  const mainGroupId = user.main_group_id;
  const mainGroupIdValue = groups.find(group => group.value === mainGroupId) || null;

  const isEmployee = type?.value.toString().includes('employee');

  const defaultValues = {
    ...user,
    date_of_birth: user.date_of_birth ? new Date(user.date_of_birth) : undefined,
    first_working_day: user.first_working_day ? new Date(user.first_working_day) : undefined,
    last_working_day: user.last_working_day ? new Date(user.last_working_day) : undefined,
    id_card_issued_at: user.id_card_issued_at ? new Date(user.id_card_issued_at) : undefined,
    id_card_expires_at: user.id_card_expires_at ? new Date(user.id_card_expires_at) : undefined,
    type,
    education,
    account_status: accountStatus,
    groups,
    main_group_id: mainGroupIdValue,
    group_rights: groupRights,
    roles,
    working_hours: workingHours,
    working_type: workingType,
    nkpd_code: nkpdCode,
    leave_notice_deadline: leaveNoticeDeadline,
    probation_period: probationPeriod,
    probation_beneficiary: probationBeneficiary,
    legal_relationship: legalRelationship,
    administrative_unit: administrativeUnit,
    position_level: positionLevel,
    position_level_info: positionLevelInfo,
    educational_level: educationalLevel,
    rang,
    monthly_salary_level: monthlySalaryLevel,
    monthly_salary_level_degree: monthlySalaryLevelDegree,
    individual_monthly_salary: individualMonthlySalary,
    // Initialize default values for leave days only if user has employee role
    leave_days_1: isEmployee ? isIntigerOrFloat(user.user_leave_days?.[0]?.days) || 0 : undefined,
    leave_days_2: isEmployee ? isIntigerOrFloat(user.user_leave_days?.[1]?.days) || 0 : undefined,
    leave_days_3: isEmployee ? isIntigerOrFloat(user.user_leave_days?.[2]?.days) || 0 : undefined,
    leave_days_4: isEmployee ? isIntigerOrFloat(user.user_leave_days?.[3]?.days) || 0 : undefined,
    leave_days_5: isEmployee ? isIntigerOrFloat(user.user_leave_days?.[4]?.days) || 0 : undefined,
    is_leaves_module_active: client?.leaves,
  };

  const methods = useForm<IInputs>({
    defaultValues,
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [user, roleOptions, userDataOptions]);

  function useQuery() {
    return new URLSearchParams(location.search);
  }
  const query = useQuery();
  useEffect(() => {
    const isOrganizeGroupParam = query.get('isOrganizeGroup');
    if (isOrganizeGroupParam === 'true') {
      setIsOrganizeGroup(true);
    }
  }, [query]);

  useEffect(() => {
    if (onSpecialViewChange) {
      onSpecialViewChange(isOrganizeGroup || isManageGroupRoleLimits);
    }
  }, [isOrganizeGroup, isManageGroupRoleLimits, onSpecialViewChange]);

  return (
    <UpdateEntity
      id={id}
      entity="users"
      methods={methods}
      onSuccess={(res: any) => {
        const { first_name: firstName, last_name: lastName } = res.data;
        showModal({
          type: 'success',
          content: (
            <>
              {t('structure.employees.editSuccessMsg')}&nbsp;
              <span className="font-weight-bold text-break">
                {firstName} {lastName}
              </span>
              .
            </>
          ),
        });
      }}
      queryKey={buildClientEntity('user', client)}
      onSuccessPushToLink={buildClientUrl('/structure/employees', client)}
    >
      {(update: any) => {
        if (isOrganizeGroup) {
          return (
            <EmployeeOrganizeGroups
              title={t('structure.employees.editTitle')}
              methods={methods}
              handleSubmit={data => {
                const formattedData = formatDataOnSubmit(data);
                return update(formattedData);
              }}
              btnLabel={t('common.save')}
              client={client}
              roleOptions={roleOptions}
              userDataOptions={userDataOptions}
              isEdit
              setIsOrganizeGroup={setIsOrganizeGroup}
            />
          );
        }
        if (isManageGroupRoleLimits) {
          return (
            <EmployeeGroupRoleLimits
              title={t('structure.employees.editTitle')}
              methods={methods}
              btnLabel={t('common.save')}
              client={client}
              roleOptions={roleOptions}
              userDataOptions={userDataOptions}
              isEdit
              setIsManageGroupRoleLimits={setIsManageGroupRoleLimits}
            />
          );
        }
        if (userDataOptions.is_multilingual) {
          return (
            <EmployeeFormMulti
              title={t('structure.employees.editTitle')}
              methods={methods}
              handleSubmit={data => {
                const formattedData = formatDataOnSubmit(data);
                return update(formattedData);
              }}
              btnLabel={t('common.save')}
              roleOptions={roleOptions}
              client={client}
              userDataOptions={userDataOptions}
              isEdit
              setIsOrganizeGroup={setIsOrganizeGroup}
              setIsManageGroupRoleLimits={setIsManageGroupRoleLimits}
              hasHiddenSensitiveFields={user.has_hidden_sensitive_fields}
            />
          );
        }
        return (
          <EmployeeForm
            title={t('structure.employees.editTitle')}
            methods={methods}
            handleSubmit={data => {
              const formattedData = formatDataOnSubmit(data);
              return update(formattedData);
            }}
            btnLabel={t('common.save')}
            client={client}
            roleOptions={roleOptions}
            userDataOptions={userDataOptions}
            isEdit
            setIsOrganizeGroup={setIsOrganizeGroup}
            setIsManageGroupRoleLimits={setIsManageGroupRoleLimits}
            hasHiddenSensitiveFields={user.has_hidden_sensitive_fields}
          />
        );
      }}
    </UpdateEntity>
  );
};

export default EditForm;
